import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MODULE_TO_TABLE } from "../constants";
import type {
  DailyLog,
  GroomingItem,
  LearningItem,
  ModuleKey,
  NutritionItem,
  QueueItem,
  RitualSnapshot,
  SyncStatus,
  TableName,
  TaskSchedule,
  WardrobeItem,
} from "../types";
import { defaultWardrobeAddedOn, emptySnapshot } from "../lib/defaults";
import { isoNow, localDateKey } from "../lib/date";
import { newId } from "../lib/id";
import {
  clearQueue,
  getLocalSnapshot,
  getQueue,
  putLocalSnapshot,
  putQueueItem,
} from "../lib/localDb";
import {
  loadLegacyLocalStorage,
  snapshotFromUnknown,
} from "../lib/legacyMigration";
import { buildFullUploadQueue, syncWithSupabase } from "../lib/sync";
import { supabase } from "../lib/supabase";
import {
  buildLog,
  buildLogLookup,
  DEFAULT_OCCURRENCE_ID,
  defaultSchedule,
  defaultWindow,
  logLookupKey,
  logKey,
} from "../lib/schedule";

function queueItem(
  table: TableName,
  payload: Record<string, unknown>,
): QueueItem {
  return {
    id: newId(),
    table,
    op: "upsert",
    payload,
    created_at: isoNow(),
  };
}

function active<T extends { deleted_at: string | null }>(items: T[]) {
  return items.filter((item) => !item.deleted_at);
}

function updateCollection<T extends { id: string }>(collection: T[], item: T) {
  const exists = collection.some((entry) => entry.id === item.id);
  return exists
    ? collection.map((entry) => (entry.id === item.id ? item : entry))
    : [item, ...collection];
}

export function useRitualStore(userId: string | null, online: boolean) {
  const [snapshot, setSnapshot] = useState<RitualSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");
  const [syncError, setSyncError] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const syncLock = useRef(false);

  const refreshPendingCount = useCallback(async () => {
    const queue = await getQueue();
    setPendingCount(queue.length);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function boot() {
      const seeded =
        (await getLocalSnapshot()) ??
        loadLegacyLocalStorage() ??
        emptySnapshot();
      const local = snapshotFromUnknown({ snapshot: seeded });
      if (!mounted) return;
      await putLocalSnapshot(local);
      setSnapshot(local);
      setLoading(false);
      await refreshPendingCount();
    }

    boot().catch((error) => {
      console.error(error);
      setSnapshot(emptySnapshot());
      setLoading(false);
      setSyncStatus("error");
      setSyncError("Ritual could not load local data.");
    });

    return () => {
      mounted = false;
    };
  }, [refreshPendingCount]);

  const persist = useCallback(
    async (next: RitualSnapshot, queue?: QueueItem[]) => {
      setSnapshot(next);
      await putLocalSnapshot(next);

      if (queue?.length) {
        for (const item of queue) await putQueueItem(item);
        await refreshPendingCount();
      }
    },
    [refreshPendingCount],
  );

  const syncNow = useCallback(
    async (base?: RitualSnapshot, forceFullUpload = false) => {
      const current = base ?? snapshot;
      if (!current || syncLock.current) return;

      if (!online) {
        setSyncStatus("offline");
        return;
      }

      if (!userId || !supabase) {
        setSyncStatus("idle");
        return;
      }

      syncLock.current = true;
      setSyncStatus("syncing");
      setSyncError(null);

      try {
        const localQueue = await getQueue();
        const shouldFullUpload =
          forceFullUpload ||
          current.profile.id !== userId ||
          current.last_synced_at === null;
        const queue = shouldFullUpload
          ? [...buildFullUploadQueue(current), ...localQueue]
          : localQueue;
        const synced = await syncWithSupabase(current, queue, userId);
        const normalized = snapshotFromUnknown({ snapshot: synced });
        await putLocalSnapshot(normalized);
        await clearQueue();
        setSnapshot(normalized);
        setPendingCount(0);
        setSyncStatus("synced");
      } catch (error) {
        console.error(error);
        setSyncStatus("error");
        setSyncError(
          error instanceof Error ? error.message : "Cloud sync failed.",
        );
      } finally {
        syncLock.current = false;
      }
    },
    [online, snapshot, userId],
  );

  useEffect(() => {
    if (!loading && snapshot && online) {
      void syncNow(snapshot);
    }
  }, [loading, online, snapshot?.profile.id, userId]);

  const timezone = snapshot?.profile.timezone;
  const today = useMemo(() => localDateKey(new Date(), timezone), [timezone]);
  const loggedKeys = useMemo(
    () => (snapshot ? buildLogLookup(snapshot.logs) : new Set<string>()),
    [snapshot?.logs],
  );

  const mutate = useCallback(
    async (
      makeNext: (current: RitualSnapshot) => {
        next: RitualSnapshot;
        queue: QueueItem[];
      },
    ) => {
      if (!snapshot) return;
      const { next, queue } = makeNext(snapshot);
      await persist(next, queue);
      void syncNow(next);
    },
    [persist, snapshot, syncNow],
  );

  const saveNutrition = useCallback(
    async (input: {
      id?: string;
      name: string;
      category: string;
      quantity: string;
      schedule?: TaskSchedule;
    }) =>
      mutate((current) => {
        const now = isoNow();
        const existing = current.nutrition.find((item) => item.id === input.id);
        const item: NutritionItem = {
          id: input.id ?? newId(),
          user_id: userId,
          created_at: existing?.created_at ?? now,
          updated_at: now,
          deleted_at: null,
          name: input.name.trim(),
          category: input.category,
          quantity: input.quantity.trim(),
          schedule:
            input.schedule ??
            existing?.schedule ??
            defaultSchedule("daily", today, {
              windows: [defaultWindow("Take", "13:00", "14:00")],
            }),
        };
        const next = {
          ...current,
          nutrition: updateCollection(current.nutrition, item),
        };
        return {
          next,
          queue: [
            queueItem(
              MODULE_TO_TABLE.nutrition,
              item as unknown as Record<string, unknown>,
            ),
          ],
        };
      }),
    [mutate, today, userId],
  );

  const saveGrooming = useCallback(
    async (input: {
      id?: string;
      name: string;
      interval_days: number;
      last_done?: string | null;
      schedule?: TaskSchedule;
    }) =>
      mutate((current) => {
        const now = isoNow();
        const existing = current.grooming.find((item) => item.id === input.id);
        const item: GroomingItem = {
          id: input.id ?? newId(),
          user_id: userId,
          created_at: existing?.created_at ?? now,
          updated_at: now,
          deleted_at: null,
          name: input.name.trim(),
          interval_days: Math.max(1, input.interval_days),
          last_done: input.last_done ?? existing?.last_done ?? null,
          schedule:
            input.schedule ??
            existing?.schedule ??
            defaultSchedule("interval", today, {
              interval_days: Math.max(1, input.interval_days),
              windows: [defaultWindow("Morning", "08:00", "10:00")],
            }),
        };
        const next = {
          ...current,
          grooming: updateCollection(current.grooming, item),
        };
        return {
          next,
          queue: [
            queueItem(
              MODULE_TO_TABLE.grooming,
              item as unknown as Record<string, unknown>,
            ),
          ],
        };
      }),
    [mutate, today, userId],
  );

  const saveWardrobe = useCallback(
    async (input: {
      id?: string;
      name: string;
      category: string;
      color: string;
      notes: string;
      image_url?: string;
      schedule?: TaskSchedule;
    }) =>
      mutate((current) => {
        const now = isoNow();
        const existing = current.wardrobe.find((item) => item.id === input.id);
        const item: WardrobeItem = {
          id: input.id ?? newId(),
          user_id: userId,
          created_at: existing?.created_at ?? now,
          updated_at: now,
          deleted_at: null,
          name: input.name.trim(),
          category: input.category,
          color: input.color.trim(),
          notes: input.notes.trim(),
          image_url: input.image_url?.trim() ?? existing?.image_url ?? "",
          last_worn: existing?.last_worn ?? null,
          added_on:
            existing?.added_on ??
            defaultWardrobeAddedOn(current.profile.timezone),
          schedule:
            input.schedule ??
            existing?.schedule ??
            defaultSchedule("weekly", today, {
              weekdays: [new Date().getDay()],
              windows: [defaultWindow("Wear", "08:00", "11:00")],
            }),
        };
        const next = {
          ...current,
          wardrobe: updateCollection(current.wardrobe, item),
        };
        return {
          next,
          queue: [
            queueItem(
              MODULE_TO_TABLE.wardrobe,
              item as unknown as Record<string, unknown>,
            ),
          ],
        };
      }),
    [mutate, today, userId],
  );

  const saveLearning = useCallback(
    async (input: {
      id?: string;
      title: string;
      type: string;
      category: string;
      notes: string;
      progress: number;
      active: boolean;
      schedule?: TaskSchedule;
    }) =>
      mutate((current) => {
        const now = isoNow();
        const existing = current.learning.find((item) => item.id === input.id);
        const item: LearningItem = {
          id: input.id ?? newId(),
          user_id: userId,
          created_at: existing?.created_at ?? now,
          updated_at: now,
          deleted_at: null,
          title: input.title.trim(),
          type: input.type,
          category: input.category,
          progress: Math.min(100, Math.max(0, input.progress)),
          notes: input.notes.trim(),
          active: input.active,
          started_on: existing?.started_on ?? today,
          schedule:
            input.schedule ??
            existing?.schedule ??
            defaultSchedule("daily", today, {
              windows: [defaultWindow("Learn", "20:00", "21:00")],
            }),
        };
        const next = {
          ...current,
          learning: updateCollection(current.learning, item),
        };
        return {
          next,
          queue: [
            queueItem(
              MODULE_TO_TABLE.learning,
              item as unknown as Record<string, unknown>,
            ),
          ],
        };
      }),
    [mutate, today, userId],
  );

  const saveLearningProgress = useCallback(
    async (id: string, progress: number) => {
      if (!snapshot) return;
      const item = snapshot.learning.find((entry) => entry.id === id);
      if (!item) return;
      await saveLearning({
        id,
        title: item.title,
        type: item.type,
        category: item.category,
        notes: item.notes,
        progress,
        active: progress < 100 ? item.active : false,
        schedule: item.schedule,
      });
    },
    [saveLearning, snapshot],
  );

  const softDelete = useCallback(
    async (module: ModuleKey, id: string) =>
      mutate((current) => {
        const now = isoNow();
        const collection = current[module].map((item) =>
          item.id === id ? { ...item, deleted_at: now, updated_at: now } : item,
        );
        const deleted = collection.find((item) => item.id === id);
        return {
          next: { ...current, [module]: collection },
          queue: deleted
            ? [
                queueItem(
                  MODULE_TO_TABLE[module],
                  deleted as unknown as Record<string, unknown>,
                ),
              ]
            : [],
        };
      }),
    [mutate],
  );

  const restoreItem = useCallback(
    async (module: ModuleKey, id: string) =>
      mutate((current) => {
        const now = isoNow();
        const collection = current[module].map((item) =>
          item.id === id
            ? { ...item, deleted_at: null, updated_at: now }
            : item,
        );
        const restored = collection.find((item) => item.id === id);
        return {
          next: { ...current, [module]: collection },
          queue: restored
            ? [
                queueItem(
                  MODULE_TO_TABLE[module],
                  restored as unknown as Record<string, unknown>,
                ),
              ]
            : [],
        };
      }),
    [mutate],
  );

  const markGroomingDone = useCallback(
    async (id: string, occurrenceId = DEFAULT_OCCURRENCE_ID) =>
      mutate((current) => {
        const now = isoNow();
        const collection = current.grooming.map((item) =>
          item.id === id
            ? { ...item, last_done: today, updated_at: now }
            : item,
        );
        const item = collection.find((entry) => entry.id === id);
        const log: DailyLog = buildLog(
          today,
          "grooming",
          id,
          occurrenceId,
          userId,
        );
        log.created_at = now;
        return {
          next: {
            ...current,
            grooming: collection,
            logs: current.logs.some(
              (log) =>
                log.log_date === today &&
                log.category === "grooming" &&
                log.item_id === id &&
                (log.occurrence_id || DEFAULT_OCCURRENCE_ID) === occurrenceId,
            )
              ? current.logs
              : [log, ...current.logs],
          },
          queue: [
            ...(item
              ? [
                  queueItem(
                    MODULE_TO_TABLE.grooming,
                    item as unknown as Record<string, unknown>,
                  ),
                ]
              : []),
            queueItem("daily_logs", log as unknown as Record<string, unknown>),
          ],
        };
      }),
    [mutate, today, userId],
  );

  const markWardrobeWorn = useCallback(
    async (id: string, occurrenceId = DEFAULT_OCCURRENCE_ID) =>
      mutate((current) => {
        const now = isoNow();
        const collection = current.wardrobe.map((item) =>
          item.id === id
            ? { ...item, last_worn: today, updated_at: now }
            : item,
        );
        const item = collection.find((entry) => entry.id === id);
        const log: DailyLog = buildLog(
          today,
          "wardrobe",
          id,
          occurrenceId,
          userId,
        );
        log.created_at = now;
        return {
          next: {
            ...current,
            wardrobe: collection,
            logs: current.logs.some(
              (log) =>
                log.log_date === today &&
                log.category === "wardrobe" &&
                log.item_id === id &&
                (log.occurrence_id || DEFAULT_OCCURRENCE_ID) === occurrenceId,
            )
              ? current.logs
              : [log, ...current.logs],
          },
          queue: [
            ...(item
              ? [
                  queueItem(
                    MODULE_TO_TABLE.wardrobe,
                    item as unknown as Record<string, unknown>,
                  ),
                ]
              : []),
            queueItem("daily_logs", log as unknown as Record<string, unknown>),
          ],
        };
      }),
    [mutate, today, userId],
  );

  const logToday = useCallback(
    async (
      category: ModuleKey,
      itemId: string,
      occurrenceId = DEFAULT_OCCURRENCE_ID,
    ) =>
      mutate((current) => {
        const id = logKey(today, category, itemId, occurrenceId);
        if (
          current.logs.some(
            (log) =>
              log.log_date === today &&
              log.category === category &&
              log.item_id === itemId &&
              (log.occurrence_id || DEFAULT_OCCURRENCE_ID) === occurrenceId,
          )
        ) {
          return { next: current, queue: [] };
        }
        const log: DailyLog = buildLog(
          today,
          category,
          itemId,
          occurrenceId,
          userId,
        );
        log.id = id;
        return {
          next: { ...current, logs: [log, ...current.logs] },
          queue: [
            queueItem("daily_logs", log as unknown as Record<string, unknown>),
          ],
        };
      }),
    [mutate, today, userId],
  );

  const saveProfile = useCallback(
    async (displayName: string, timezone: string) =>
      mutate((current) => {
        const profile = {
          ...current.profile,
          id: userId ?? current.profile.id,
          display_name: displayName.trim() || "Tapan",
          timezone: timezone.trim() || current.profile.timezone,
          updated_at: isoNow(),
        };
        return {
          next: { ...current, profile },
          queue: [
            queueItem(
              "profiles",
              profile as unknown as Record<string, unknown>,
            ),
          ],
        };
      }),
    [mutate, userId],
  );

  const replaceSnapshot = useCallback(
    async (next: RitualSnapshot) => {
      await putLocalSnapshot(next);
      setSnapshot(next);
      await clearQueue();
      for (const item of buildFullUploadQueue(next)) await putQueueItem(item);
      await refreshPendingCount();
      void syncNow(next, true);
    },
    [refreshPendingCount, syncNow],
  );

  const isDoneToday = useCallback(
    (
      category: ModuleKey,
      itemId: string,
      occurrenceId = DEFAULT_OCCURRENCE_ID,
    ) =>
      loggedKeys.has(logLookupKey(today, category, itemId, occurrenceId)),
    [loggedKeys, today],
  );

  const visible = useMemo(() => {
    if (!snapshot) return null;
    return {
      nutrition: active(snapshot.nutrition),
      grooming: active(snapshot.grooming),
      wardrobe: active(snapshot.wardrobe),
      learning: active(snapshot.learning),
    };
  }, [snapshot]);

  return {
    snapshot,
    visible,
    loading,
    syncStatus,
    syncError,
    pendingCount,
    today,
    loggedKeys,
    saveNutrition,
    saveGrooming,
    saveWardrobe,
    saveLearning,
    saveLearningProgress,
    softDelete,
    restoreItem,
    markGroomingDone,
    markWardrobeWorn,
    logToday,
    isDoneToday,
    saveProfile,
    replaceSnapshot,
    syncNow,
  };
}
